import { BlogBodyComponent, Block, TextNode, LinkNode } from "@/shared/models/content/blog-article-content";

/**
 * Cleans text from markdown formatting like ** bold **, removing stars
 * Also cleans newlines and extra whitespace
 */
export const cleanTextFormatting = (text: string): string => {
  if (!text) return "";

  return (
    text
      // Remove markdown bold formatting (**texttt**)
      .replace(/\*\*(.*?)\*\*/g, "$1")
      // Remove markdown italic formatting (*text*)
      .replace(/\*(.*?)\*/g, "$1")
      // Replace newlines with spaces
      .replace(/\n/g, " ")
      // Replace multiple spaces with single space
      .replace(/\s+/g, " ")
      // Trim whitespace
      .trim()
  );
};

/**
 * Extracts text content from blocks (new format)
 */
const extractTextFromBlocks = (blocks: Block[]): string => {
  let text = "";

  const extractTextFromChildren = (children: (TextNode | LinkNode)[]): string => {
    return children
      .map((child) => {
        if (child.type === "text") {
          return child.text;
        } else if (child.type === "link") {
          return child.children.map((linkChild) => linkChild.text).join("");
        }
        return "";
      })
      .join("");
  };

  for (const block of blocks) {
    switch (block.type) {
      case "heading":
      case "paragraph":
        text += extractTextFromChildren(block.children) + " ";
        break;
      case "list":
        for (const item of block.children) {
          text += extractTextFromChildren(item.children) + " ";
        }
        break;
    }
  }

  return text.trim();
};

/**
 * Extracts text content from BlogBodyComponent array
 * Takes first X characters for descriptions
 */
export const extractContentFromBody = (body: BlogBodyComponent[], maxLength: number = 160): string => {
  if (!body || body.length === 0) return "";

  let extractedText = "";

  for (const component of body) {
    if (component.__component === "blog.blog-content" && component.content) {
      let plainText = "";

      // Handle both string and block formats
      if (typeof component.content === "string") {
        // Old format: string content
        plainText = component.content
          .replace(/<[^>]*>/g, " ") // Remove HTML tags
          .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
          .replace(/&[^;]+;/g, " ") // Replace other HTML entities
          .replace(/\s+/g, " ") // Replace multiple spaces with single space
          .trim();
      } else {
        // New format: block content
        plainText = extractTextFromBlocks(component.content);
      }

      extractedText += plainText + " ";

      // Break if we have enough content
      if (extractedText.length >= maxLength) {
        break;
      }
    }
  }

  // Clean the extracted text and truncate
  const cleanedText = cleanTextFormatting(extractedText);

  if (cleanedText.length <= maxLength) {
    return cleanedText;
  }

  // Truncate at word boundary
  const truncated = cleanedText.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + "...";
  }

  return truncated + "...";
};
